<template>
  <div :class="['home flex-column', programStyle]">
    <div class="upper">
      <div class="title">Appeal Details</div>
    </div>
    <div class="appeal-details-table flex-row">
      <div class="table-container">
        <div class="table-name"><h3>Dealer</h3></div>
        <DataTable :headings="dealerAppealsTH" :rows="appealsData" :perPage="rowCount" :page="currentPage">
          <template v-slot:appealResultSlot="slotProps">
            <Multiselect
              v-model="slotProps.row.appealResult"
              :options="appealOptions"
              class="multiselect single"
              :showLabels="false"
              :allow-empty="false"
              :disabled="!slotProps.row.isDealerAppealSubmittable || appView === 'field'"
              :placeholder="''"
            ></Multiselect>
          </template>
          <template v-slot:appealReasonSlot="slotProps">
            <textarea
              name="appeal-reason-box"
              id=""
              cols="30"
              rows="10"
              class="reason-box"
              :class="[
                appView === 'dealer' && slotProps.row.appealResult === 'Yes' && !slotProps.row.appealReason
                  ? 'required'
                  : '',
              ]"
              placeholder="Enter appeal reason"
              v-model="slotProps.row.appealReason"
              :disabled="
                !slotProps.row.isDealerAppealSubmittable || appView === 'field' || slotProps.row.appealResult === 'No'
              "
            ></textarea>
          </template>
        </DataTable>
      </div>
      <div class="table-container">
        <div class="table-name"><h3>HQ</h3></div>
        <DataTable
          :headings="hqAppealsTH"
          :rows="appealsData"
          :perPage="rowCount"
          :page="currentPage"
          :sortedKeyName="'vin'"
        >
          <template v-slot:reviewResultSlot="slotProps">
            <Multiselect
              v-model="slotProps.row.reviewResult"
              :options="reviewOptions"
              class="multiselect single"
              :showLabels="false"
              :allow-empty="false"
              :disabled="
                !canReview || slotProps.row.appealResult === 'No' || slotProps.row.reviewedAt || appView === 'dealer'
              "
              :placeholder="''"
            ></Multiselect>
          </template>
          <template v-slot:reviewReasonSlot="slotProps">
            <textarea
              name="review-reason-box"
              id=""
              cols="30"
              rows="10"
              class="reason-box"
              :class="[
                appView === 'field' &&
                slotProps.row.reviewResult &&
                slotProps.row.reviewResult !== 'No Action' &&
                !slotProps.row.reviewReason
                  ? 'required'
                  : '',
              ]"
              placeholder="Enter review reason"
              v-model="slotProps.row.reviewReason"
              :disabled="
                !canReview ||
                slotProps.row.appealResult === 'No' ||
                slotProps.row.reviewResult === 'No Action' ||
                slotProps.row.reviewedAt ||
                appView === 'dealer'
              "
            ></textarea>
          </template>
        </DataTable>
      </div>
    </div>
    <div v-if="appView === 'field'" class="row-container pagination">
      <div class="row-info">
        {{ $t('viewing-firstrow-lastrow-of-forms-totalcount', [firstRow, lastRow, totalCount]) }}
      </div>
      <GPagination
        v-if="pageCount > 1"
        class="pagination"
        v-model="page"
        :pageCount="pageCount"
        @changed="getAppealsData"
      />
    </div>
    <div class="warning">
      <h4>
        Any Survey Interference or Survey Removal appeal without a sufficient <b>Appeal Reason</b> will be denied.
      </h4>
    </div>
    <div class="upload-container" v-if="this.canUpload || appealsUploads.length > 0">
      <h4 class="section-title">SUPPORTING DOCUMENTS</h4>
      <Upload
        v-if="this.canUpload"
        :appeal="appealsData"
        :isSurveyInterference="true"
        v-on:sendurl="getUrl"
        @removeUpload="removeUpload"
      />
      <div class="uploaded-documents" v-if="appealsUploads.length > 0">
        <h4>Uploaded:</h4>
        <div class="support-docs" v-for="upload in appealsUploads" :key="upload.key">
          <span>{{ upload.originalFileName }}</span>
          <a class="relative" target="_blank" :href="upload.assetUrl">
            <img class="download-icon" src="@/assets/img/download-arrow.png" />
          </a>
        </div>
      </div>
    </div>
    <div class="warning">
      <h4>Use this area if you need to provide additional evidence to support your appeal.</h4>
      <h4>
        Note: Each Survey appeals requires an <b>Appeal Reason</b>; adding supporting documents does not replace the
        need for the <b>Appeal Reason</b>.
      </h4>
    </div>
    <div class="button-container">
      <button :class="[submitValidation ? '' : 'disabled']" :disabled="!submitValidation" @click="finalValidation">
        Save Changes
      </button>
      <button class="cancel-button" @click="setModalVisibility">{{ $t('exit') }}</button>
    </div>
    <Modal class="modal-screens" v-show="isModalOpen">
      <template v-slot:body class="body">
        <div class="modal-content">
          <span class="modal-title">Confirm exit?</span>
          <span class="modal-text"
            >You are leaving this page. Once confirmed, your progress will be lost until you save your changes. Are you
            sure you want to exit?</span
          >
        </div>
        <div class="modal-button-container">
          <button class="submit" @click="exit">Confirm</button>
          <button class="cancel-button" @click="isModalOpen = false">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue';
import Multiselect from 'vue-multiselect';
import Modal from '@/components/Modal.vue';
import GPagination from '@/components/GComponents/GPagination.vue';
import Upload from '@/components/Upload.vue';
import dealerAppealsTH from '@/utils/tableHeaders/surveyinterference/dealerAppealsTH';
import hqAppealsTH from '@/utils/tableHeaders/surveyinterference/hqAppealsTH';
import { mapState } from 'vuex';

export default {
  name: 'AppealDetails',
  components: {
    DataTable,
    Multiselect,
    Modal,
    GPagination,
    Upload,
  },
  props: {
    entity: {
      type: String,
    },
  },
  data() {
    return {
      rowCount: 10,
      dealerAppealsTH: dealerAppealsTH(),
      hqAppealsTH: hqAppealsTH(),
      appealsData: [],
      appealsUploads: [],
      page: 1,
      numPerPage: 10,
      currentPage: 1,
      totalCount: null,
      rowCountDropValue: { label: this.$t('show-10'), value: 10 },
      appealOptions: ['No', 'Yes'],
      reviewOptions: ['No Action', 'Approve', 'Deny', 'Switch'],
      isModalOpen: false,
      appView: '',
      canReview: false,
      uploads: [],
      canUpload: false,
    };
  },
  methods: {
    setModalVisibility() {
      if (this.submitValidation) {
        this.isModalOpen = true;
      } else {
        this.exit();
      }
    },
    exit() {
      if (this.$route.name === 'Appeal-Details-Field') {
        this.$router.push({
          name: 'Summary',
        });
      } else {
        this.$router.push({
          name: 'DealerSI',
        });
      }
    },
    getAppealsData() {
      const loader = this.$loading.show();

      const { entity } = this;
      const page = this.page - 1;
      const { period, letterDate, letterType, market } = this.$route.params;

      let actionName = null;
      if (this.$route.name === 'Appeal-Details-Field') {
        actionName = 'getHqAppealsData';
      } else {
        actionName = 'getDealerAppealsData';
      }

      this.$store
        .dispatch(actionName, { entity, page, period, letterDate, letterType, market })
        .then((res) => {
          this.canReview = res.allSIAppeals.canReview;
          this.appealsData = res.allSIAppeals.resultArr;
          this.appealsUploads = res.allSIAppeals.assetUrlArray;
          this.canUpload = res.allSIAppeals.canUpload;
          this.totalCount = res.allSIAppeals.totalReviewLtrCount;
          this.setDropdownValues();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    setUpAppeal() {
      const loader = this.$loading.show();

      let allAppealedSIArray = [];
      let allReviewedSIArray = [];

      if (this.$route.name === 'Appeal-Details-Field') {
        this.appealsData.forEach((appeal) => {
          if (appeal.reviewResult && appeal.reviewReason) {
            allReviewedSIArray.push({
              surveyInterferenceId: appeal.id,
              reviewResult: appeal.reviewResult,
              reviewReason: appeal.reviewReason,
            });
          }
        });
      } else {
        this.appealsData.forEach((appeal) => {
          if (appeal.appealResult && appeal.appealReason) {
            allAppealedSIArray.push({
              surveyInterferenceId: appeal.id,
              appealResult: appeal.appealResult,
              appealReason: appeal.appealReason,
            });
          }
        });
      }

      let params = null;

      if (this.$route.name === 'Appeal-Details-Field') {
        params = {
          allReviewedSIArray,
        };
      } else {
        params = {
          allAppealedSIArray,
          siUploadsArray: this.uploads,
        };
      }

      this.submitAppeal(params);

      allAppealedSIArray = [];
      allReviewedSIArray = [];

      loader.hide();
    },
    submitAppeal(params) {
      const loader = this.$loading.show();

      let actionName = null;

      if (this.$route.name === 'Appeal-Details-Field') {
        actionName = 'submitHqReview';
      } else {
        actionName = 'submitDealerSI';
      }

      this.$store
        .dispatch(actionName, params)
        .then(() => {
          this.$notify({
            group: 'primary',
            type: 'success',
            text: 'Appeal Successfully Saved!',
          });
          this.exit();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    setDropdownValues() {
      this.appealsData.forEach((appeal) => {
        // set 'No' as default value for appealResult
        if (!appeal.appealResult) {
          appeal.appealResult = 'No';
        }
        // set 'No Action' as default value for reviewResult
        if (!appeal.reviewResult) {
          appeal.reviewResult = 'No Action';
        }
      });
    },
    appealValidation() {
      if (this.appealsData.length > 0) {
        // Dealer view
        if (this.appView === 'dealer') {
          // Setup appeal reason for each appeal.
          this.appealsData.forEach((appeal) => {
            if (appeal.appealResult === 'No') {
              appeal.appealReason = null;
            }
          });
          // Validation logic
          if (
            this.appealsData.some(
              (appeal) =>
                appeal.appealResult &&
                appeal.appealResult === 'Yes' &&
                appeal.appealReason &&
                appeal.isDealerAppealSubmittable,
            )
          ) {
            return true;
          }
          return false;
        }
        // Field view
        if (this.appView === 'field') {
          // Setup review reason for each appeal.
          this.appealsData.forEach((appeal) => {
            if (appeal.reviewResult === 'No Action') {
              appeal.reviewReason = null;
            }
          });
          // Validation logic
          if (
            this.appealsData.some(
              (appeal) =>
                this.canReview &&
                appeal.reviewResult &&
                appeal.reviewResult !== 'No Action' &&
                appeal.reviewReason &&
                !appeal.reviewedAt,
            )
          ) {
            return true;
          }
          return false;
        }
      }
      return false;
    },
    goToPrograms() {
      this.$store.state.global.programName = null;
      this.$store.state.global.programImage = null;
      this.$store.state.global.programStyle = null;
      this.$router.push({
        name: 'Programs',
      });
      // eslint-disable-next-line prefer-destructuring
      this.$store.state.activeNavTab = this.activeNavBar[0];
    },
    finalValidation() {
      // Dealer view
      if (this.appView === 'dealer') {
        // Setup arrays
        const totalSubmittableAppeals = this.appealsData.filter(
          (appeal) => appeal.appealResult && appeal.appealResult === 'Yes' && appeal.isDealerAppealSubmittable,
        );
        const submittableAppeals = this.appealsData.filter(
          (appeal) =>
            appeal.appealResult &&
            appeal.appealResult === 'Yes' &&
            appeal.appealReason &&
            appeal.isDealerAppealSubmittable,
        );

        // Submit appeal if appeal reason is entered for all submittable appeals
        if (submittableAppeals.length === totalSubmittableAppeals.length) {
          this.setUpAppeal();
        } else {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: 'Please double check the highlighted fields and try again.',
          });
        }
      }
      // Field view
      if (this.appView === 'field') {
        // Setup arrays
        const totalReviewableAppeals = this.appealsData.filter(
          (appeal) => appeal.reviewResult && appeal.reviewResult !== 'No Action' && !appeal.reviewedAt,
        );
        const reviewableAppeals = this.appealsData.filter(
          (appeal) =>
            appeal.reviewResult && appeal.reviewResult !== 'No Action' && appeal.reviewReason && !appeal.reviewedAt,
        );

        // Submit review if review reason is entered for all reviewable appeals
        if (reviewableAppeals.length === totalReviewableAppeals.length) {
          this.setUpAppeal();
        } else {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: 'Please double check the highlighted fields and try again.',
          });
        }
      }
    },
    getUrl(res) {
      const upload = {
        adjustedFilename: res.res.data.fileInfo.adjustedFilename,
        originalFileName: res.res.data.fileInfo.originalFilename,
        label: res.appeal.dialTitle,
        dialId: res.appeal.dialId,
      };
      this.uploads.push(upload);
    },
    removeUpload(selectedFile) {
      this.uploads.forEach((file, index) => {
        if (file.originalFilename === selectedFile.file) {
          this.uploads.splice(index, 1);
        }
      });
    },
  },
  computed: {
    ...mapState('global', ['programStyle', 'programName']),
    ...mapState(['activeNavBar', 'activeNavTab']),
    domain() {
      return this.programName;
    },
    pageCount() {
      if (this.appealsData) {
        const l = this.totalCount;
        const s = this.rowCountDropValue.value;
        return Math.ceil(l / s);
      }
      return null;
    },
    firstRow() {
      if (this.appealsData.length > 0) {
        return (this.page - 1) * 10 + 1;
      }
      return 0;
    },
    lastRow() {
      return (this.page - 1) * 10 + this.appealsData.length;
    },
    submitValidation() {
      return this.appealValidation();
    },
  },
  created() {
    if (this.$route.name === 'Appeal-Details-Field') {
      this.appView = 'field';
    } else {
      this.appView = 'dealer';
    }
    this.getAppealsData();
  },
  beforeDestroy() {
    this.$emit('exit');
  },
};
</script>

<style lang="scss" scoped>
.home {
  width: 100%;
  .title {
    font-size: 32px;
    line-height: 38px;
    font-weight: bold;
    text-transform: uppercase;
  }
  .subtitle {
    font-size: 14px;
    margin-top: 5px;
    .breadcrumb {
      margin-right: 5px;
      cursor: pointer;
    }
    .breadcrumb-title {
      font-weight: bold;
    }
  }
}

.logo {
  vertical-align: middle;
  width: 400px;
  margin-top: 35px;
}
.title-container {
  text-align: left;
  border-bottom: 1px #dfe4e8 solid;
  padding-left: 40px;
  padding-bottom: 25px;
  padding: 30px 50px 30px 30px;
  justify-content: space-between;
  .right {
    display: flex;
  }
}

input {
  width: 300px;
  outline: 0;
}

.placeholder-div {
  width: 130px;
}
.empty-program {
  margin-top: 20px;
}
.upper {
  padding: 30px;
  text-align: left;
  .title {
    font-size: 18px;
    line-height: 24px;
    color: $primary;
  }
}
.table-name-container {
  width: 100%;
  .dealer-table,
  .hq-table {
    background-color: $primary;
    color: $primary-white;
    text-align: center;
    border-bottom: 1px solid $primary-white;
    font-size: 14px;
    line-height: 20px;
    font-weight: bold;
    padding: 10px 0px;
    text-transform: uppercase;
    border-right: 1px solid $input-readonly;
  }
  .dealer-table {
    width: 75%;
  }
  .hq-table {
    width: 25%;
  }
}
.appeal-details-table {
  width: 100%;
  overflow-x: auto;
  .table-name {
    text-align: center;
    h3 {
      background-color: $primary;
      color: $primary-white;
      border-bottom: 1px solid $primary-white;
      font-size: 14px;
      line-height: 20px;
      font-weight: bold;
      padding: 10px 0px;
      text-transform: uppercase;
      border-right: 1px solid $input-readonly;
      margin: 0px;
    }
  }
  .required {
    border: 1px solid red !important;
  }
  .reason-box {
    height: 24px !important;
    width: 302px !important;
    padding: 10px 5px 10px 10px !important;
    overflow-y: auto;
    resize: none !important;
    outline: none;
    margin: 0px;
  }
  .reason-box::-webkit-scrollbar {
    -webkit-appearance: none;
  }
  .reason-box::-webkit-scrollbar:vertical {
    width: 5px;
  }
  .reason-box::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 1px solid white;
    background-color: $input-readonly-text;
  }
  .reason-box:read-only::-webkit-scrollbar-thumb {
    border: 1px solid $input-readonly;
  }
}
.appeal-details-table::-webkit-scrollbar {
  -webkit-appearance: none;
}
.appeal-details-table::-webkit-scrollbar:horizontal {
  width: 2px !important;
  height: 9px;
  background-color: $light-grey;
}
.appeal-details-table::-webkit-scrollbar-thumb {
  border-radius: 8px;
  border: 1px solid $light-grey;
  background-color: $border;
}
.pagination {
  margin: 15px 30px 0px;
}
.warning {
  margin: 0px 30px;
  h4 {
    font-size: 14px;
    line-height: 20px;
    color: $input-readonly-text;
    text-transform: none;
    margin: 15px 0px 0px;
  }
  h4:nth-child(2) {
    margin-top: 0px;
  }
}
.upload-container {
  margin: 0px 30px;
}
.upload-container {
  font-size: 14px;
  .section-title {
    font-size: 18px;
    line-height: 24px;
    color: $primary;
    font-weight: bold;
    margin: 30px 0px;
  }
  .title {
    font-size: 14px;
    color: $primary-black;
    font-weight: bold;
    line-height: 20px;
    text-transform: uppercase;
    margin: 0px;
  }
  .support-docs {
    display: flex;
    align-items: center;
  }
  .uploaded-documents {
    margin-top: 15px;
    h4 {
      font-size: 14px;
      line-height: 20px;
      text-transform: none;
      margin: 0px;
      font-weight: bold;
    }
  }
}
.button-container {
  display: flex;
  margin: 45px 30px;
  align-items: center;
  justify-content: flex-start;
  button {
    margin-right: 15px;
  }
  .cancel-button {
    background-color: $primary-white;
    border: 1px solid $primary;
    color: $primary;
  }
}
.modal-button-container {
  display: flex;
  margin-top: 34px;
  justify-content: center;
  .submit {
    margin-right: 20px;
  }
}
.modal-body {
  display: flex;
  flex-direction: column;
  .close {
    display: flex;
    justify-content: flex-end;
    svg {
      height: 30px;
      width: 30px;
      cursor: pointer;
    }
  }
  .modal-content {
    width: 100%;
    text-align: center;
  }
  .modal-title {
    font-size: 20px;
    text-transform: uppercase;
    font-weight: bold;
    display: block;
    margin-bottom: 20px;
    color: $primary;
  }
  .modal-text {
    font-size: 14px;
    color: $primary-black;
    display: block;
  }
}
</style>
